/**
 * TenantPortalLayout
 *
 * Branded investor portal for sandbox tenants.
 * Route: /:tenantSlug/portal/*
 *
 * Renders the same investor portal pages (Invest, Portfolio, Transactions,
 * Settings) but with the tenant's branding (logo, colors, company name)
 * instead of REDEX branding. REDEX is never surfaced to sandbox investors.
 *
 * Authentication: JWT must carry sandboxTenantId matching the URL slug.
 * If the user is not authenticated or belongs to a different tenant,
 * they are redirected to /:tenantSlug/login.
 */

import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { Menu, Settings, LogOut, User, ChevronDown } from 'lucide-react';
import { Button } from '../components/new-components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/new-components/ui/sheet';
import { TenantBrandingProvider, useTenantBranding } from '../contexts/TenantBrandingContext';
import { useUser } from '../contexts/UserContext';
import { confirmSignOutWithUnsent } from '../config/authRoutes';
import LoadingSpinner from '../components/UI/LoadingSpinner';

// ─── Guarded portal content ─────────────────────────────────────────────────
function TenantPortalContent() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { branding, isLoading: brandingLoading, notFound } = useTenantBranding();
  const { isAuthenticated, isLoading: authLoading, user, logout, sandboxTenantId } = useUser();

  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Auth loading
  if (authLoading || brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Tenant doesn't exist
  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-semibold mb-2">Portal not found</h1>
          <a href="/login" className="text-sm text-blue-600 hover:underline">Back to login</a>
        </div>
      </div>
    );
  }

  // Not authenticated → go to this tenant's login
  if (!isAuthenticated) {
    return <Navigate to={`/${tenantSlug}/login`} replace state={{ from: location }} />;
  }

  // Authenticated user belongs to a different tenant — refuse cross-tenant access
  if (sandboxTenantId !== tenantSlug) {
    return <Navigate to={`/${tenantSlug}/login`} replace />;
  }

  const primary = branding?.primaryColor || '#0f172a';
  const accent  = branding?.accentColor  || '#3b82f6';
  const fontFamily = branding?.fontFamily || 'Inter, sans-serif';
  const companyName = branding?.companyName || tenantSlug || 'Portal';
  const logoUrl = branding?.logoUrl || null;

  // Compute the sub-path base (e.g. /:tenantSlug/portal)
  const base = `/${tenantSlug}/portal`;

  function isActive(section: string) {
    return location.pathname.startsWith(`${base}/${section}`);
  }

  async function handleLogout() {
    if (!confirmSignOutWithUnsent(user?.id)) return; // unsent notes on this computer: ask first
    await logout();
    navigate(`/${tenantSlug}/login`);
  }

  const navItems = [
    { label: 'Invest',       path: `${base}/invest/properties` },
    { label: 'Portfolio',    path: `${base}/portfolio` },
    { label: 'Transactions', path: `${base}/payments` },
  ];

  return (
    <div
      className="min-h-screen bg-background"
      style={{ fontFamily }}
    >
      {/* Header */}
      <header
        className="border-b border-border sticky top-0 z-50"
        style={{ backgroundColor: primary }}
      >
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-6">
            {/* Tenant logo / name */}
            <div
              className="flex items-center flex-shrink-0 cursor-pointer"
              onClick={() => navigate(`${base}/invest/properties`)}
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="h-10 w-auto object-contain"
                  style={{ filter: 'brightness(0) invert(1)' }} // white logo on colored bg
                />
              ) : (
                <span className="text-white text-lg font-bold tracking-tight">
                  {companyName}
                </span>
              )}
            </div>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-5 ml-2">
              {navItems.map(({ label, path }) => {
                const section = path.split('/').pop()!;
                const active = isActive(section) || (section === 'invest' && location.pathname === base);
                return (
                  <button
                    key={label}
                    onClick={() => navigate(path)}
                    className={`py-1.5 text-sm font-medium transition-all relative ${
                      active
                        ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-white'
                        : 'text-white/70 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </nav>

            {/* User dropdown — desktop */}
            <div className="hidden md:flex items-center ml-auto relative">
              <Button
                variant="ghost"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 text-white hover:text-white hover:bg-white/10"
              >
                <User className="w-4 h-4" />
                <span className="text-sm">{user?.email?.split('@')[0] || 'User'}</span>
                <ChevronDown className="w-3 h-3" />
              </Button>

              {dropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-xl shadow-lg py-1 z-50">
                  <button
                    onClick={() => { navigate(`${base}/settings`); setDropdownOpen(false); }}
                    className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm"
                  >
                    <Settings className="w-4 h-4" />
                    Settings
                  </button>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2 text-sm text-red-600"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu */}
            <div className="md:hidden ml-auto">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/10">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-64">
                  <div className="flex flex-col gap-1 mt-6">
                    {navItems.map(({ label, path }) => (
                      <button
                        key={label}
                        onClick={() => { navigate(path); setMenuOpen(false); }}
                        className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted text-sm font-medium"
                      >
                        {label}
                      </button>
                    ))}
                    <hr className="my-2" />
                    <button
                      onClick={() => { navigate(`${base}/settings`); setMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted text-sm flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted text-sm text-red-600 flex items-center gap-2"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Accent-colored top strip under header */}
      <div className="h-0.5" style={{ backgroundColor: accent }} />

      {/* Page content — rendered by child routes */}
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>

      {/* Footer — no REDEX branding visible to tenant users */}
      <footer className="border-t border-border py-6 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">
            © {new Date().getFullYear()} {companyName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

// ─── Outer wrapper with branding provider ────────────────────────────────────
export default function TenantPortalLayout() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();

  if (!tenantSlug) return <Navigate to="/login" replace />;

  return (
    <TenantBrandingProvider slug={tenantSlug}>
      <TenantPortalContent />
    </TenantBrandingProvider>
  );
}
